// src/handover/handoverUtils.js
import { uploadData, remove, isCancelError } from 'aws-amplify/storage';

function makeUploadId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `upl_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function sanitizeFileName(name) {
  return String(name || 'file')
    .replace(/[^\w.-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '') || 'file';
}

function makeUploadFingerprint(file) {
  return [
    file?.name || 'file',
    file?.size || 0,
    file?.lastModified || 0,
    file?.type || '',
  ].join('__');
}

export function fuelStringToPercent(value) {
  switch (value) {
    case '1/4':
      return 25;
    case '1/2':
      return 50;
    case '3/4':
      return 75;
    case 'full':
      return 100;
    default:
      return null;
  }
}

export function buildUploadKey(prefix, reservationId, fileName) {
  const safePrefix = String(prefix || '').replace(/\/+$/, '');
  const safeReservationId = String(reservationId || '').trim();
  const stamp = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  return `${safePrefix}/${safeReservationId}/${stamp}_${sanitizeFileName(fileName)}`;
}

export function createPendingUpload(file) {
  return {
    id: makeUploadId(),
    fingerprint: makeUploadFingerprint(file),
    name: file?.name || 'file',
    size: Number(file?.size || 0),
    type: file?.type || '',
    file: file || null,
    key: null,
    status: 'queued',
    progress: 0,
    error: '',
    previewUrl: file ? URL.createObjectURL(file) : '',
  };
}

export function startUpload({ prefix, reservationId, file, onProgress }) {
  const key = buildUploadKey(prefix, reservationId, file?.name);
  const task = uploadData({
    key,
    data: file,
    options: {
      contentType: file?.type || undefined,
      onProgress,
    },
  });

  return {
    key,
    task,
  };
}

export async function removeUploadedFile(key) {
  if (!key) return;
  await remove({ key });
}

export function uploadedItemsToKeys(items) {
  return (Array.isArray(items) ? items : [])
    .filter((item) => item?.status === 'uploaded' && item?.key)
    .map((item) => item.key);
}

export function countPendingUploads(items) {
  return (Array.isArray(items) ? items : []).filter((item) =>
    ['queued', 'uploading', 'removing'].includes(String(item?.status || ''))
  ).length;
}

export function countFailedUploads(items) {
  return (Array.isArray(items) ? items : []).filter(
    (item) => String(item?.status || '') === 'error'
  ).length;
}

export async function uploadFiles(prefix, reservationId, files) {
  const keys = [];
  for (const file of files || []) {
    if (!file) continue;
    const { key, task } = startUpload({ prefix, reservationId, file });
    try {
      await task.result;
      keys.push(key);
    } catch (error) {
      if (isCancelError(error)) continue;
      throw error;
    }
  }
  return keys;
}

export function dataUrlToFile(dataUrl, filename) {
  if (!dataUrl) return null;
  const arr = dataUrl.split(',');
  if (arr.length < 2) return null;
  const match = arr[0].match(/:(.*?);/);
  const mime = match ? match[1] : 'image/png';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
}

export { isCancelError };
